import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Subscription, SubscriptionCost } from '@/hooks/useSubscriptions';
import { SubscriptionBadge } from './SubscriptionBadge';
import { format, differenceInDays } from 'date-fns';
import { CalendarDays, DollarSign, Users, AlertTriangle } from 'lucide-react';

interface SubscriptionDetailsProps {
  subscription: Subscription;
  cost: SubscriptionCost;
}

export function SubscriptionDetails({ subscription, cost }: SubscriptionDetailsProps) {
  const daysUntilBilling = differenceInDays(new Date(subscription.next_billing_date), new Date());

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Plan Details</CardTitle>
            <SubscriptionBadge status={subscription.subscription_status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-sm text-muted-foreground">Plan Name</div>
            <div className="text-lg font-medium">{subscription.plan_name}</div>
          </div>

          {subscription.trial_end_date && subscription.subscription_status === 'trial' && (
            <div className="bg-secondary/50 p-3 rounded-md">
              <div className="text-sm font-medium">Trial Period</div>
              <div className="text-sm text-muted-foreground">
                Ends {format(new Date(subscription.trial_end_date), 'MMM dd, yyyy')}
              </div>
            </div>
          )}

          {subscription.notes && (
            <div>
              <div className="text-sm text-muted-foreground">Notes</div>
              <div className="text-sm">{subscription.notes}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Cost Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Base Monthly Fee:</span>
            <span className="font-medium">${cost.base_fee.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Per User Fee (${cost.per_user_fee.toFixed(2)} × {cost.licensed_user_count} licensed):
            </span>
            <span className="font-medium">${(cost.per_user_fee * cost.licensed_user_count).toFixed(2)}</span>
          </div>
          <div className="border-t border-border pt-3 flex justify-between">
            <span className="font-semibold">Total Monthly Cost:</span>
            <span className="text-xl font-bold text-primary">${cost.total_monthly_cost.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Annual Projection:</span>
            <span>${(cost.total_monthly_cost * 12).toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Billing Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Billing Cycle Start:</span>
            <span>{format(new Date(subscription.billing_cycle_start_date), 'MMM dd, yyyy')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Next Billing Date:</span>
            <span className="font-medium">{format(new Date(subscription.next_billing_date), 'MMM dd, yyyy')}</span>
          </div>
          <div className="bg-muted p-3 rounded-md text-sm">
            <span className="text-muted-foreground">
              {daysUntilBilling > 0 ? `${daysUntilBilling} days until next billing` : 'Billing due'}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-4 w-4" />
            License Utilization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Active Users:</span>
            <span className="font-medium">{cost.actual_user_count} / {cost.licensed_user_count} licensed</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Utilization:</span>
            <span className="font-medium">
              {cost.licensed_user_count > 0 
                ? ((cost.actual_user_count / cost.licensed_user_count) * 100).toFixed(0)
                : 0}%
            </span>
          </div>
          {cost.actual_user_count > cost.licensed_user_count && (
            <div className="mt-2 p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Over licensed capacity! Consider upgrading to {cost.actual_user_count} licenses.</span>
            </div>
          )}
          {cost.actual_user_count === cost.licensed_user_count && (
            <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-sm text-yellow-700 dark:text-yellow-500 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>At full capacity. Consider adding more licenses before onboarding new users.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
